import { clamp, el } from './util.js';

// Deliberately conservative: touchscreen laptops still keep the desktop HUD,
// while phones and tablets get controls sized for a thumb.
const MOBILE_QUERY = '(hover: none) and (pointer: coarse)';

/**
 * Two persistent virtual sticks plus hold-to-fire / hold-to-ADS buttons.
 * This module only owns DOM and pointer handling; it writes normalized values
 * into Input so gameplay consumes mobile, gamepad, and desktop input alike.
 */
export class TouchControls {
  constructor(parent, input) {
    this.input = input;
    this.active = false;
    this.media = matchMedia?.(MOBILE_QUERY);

    this.root = el('div', 'ow-touch-controls', parent);
    this.root.setAttribute('aria-hidden', 'true');
    this.root.hidden = true;

    this.sticks = [
      this._makeStick('move', 'Move'),
      this._makeStick('look', 'Look'),
    ];
    this.buttons = [
      this._makeButton('fire', 'Fire'),
      this._makeButton('ads', 'Aim'),
    ];

    this._onMediaChange = () => this._sync();
    this.media?.addEventListener?.('change', this._onMediaChange);
    this._onBlur = () => this._reset();
    addEventListener('blur', this._onBlur);
    this._sync();
  }

  get isTouchDevice() {
    return !!this.media?.matches;
  }

  setActive(active) {
    this.active = !!active;
    this._sync();
  }

  _sync() {
    const visible = this.active && this.isTouchDevice;
    this.root.hidden = !visible;
    this.root.classList.toggle('ow-touch-active', visible);
    this.root.setAttribute('aria-hidden', String(!visible));
    if (!visible) this._reset();
  }

  _makeStick(kind, label) {
    const node = el('div', `ow-touch-stick ow-touch-stick-${kind}`, this.root);
    node.setAttribute('role', 'group');
    node.setAttribute('aria-label', `${label} joystick`);
    el('div', 'ow-touch-stick-ring', node);
    const thumb = el('div', 'ow-touch-stick-thumb', node);
    el('span', 'ow-touch-stick-label', node, label);
    const state = { kind, node, thumb, pointerId: null };

    const down = (e) => {
      if (!this.active || state.pointerId !== null) return;
      state.pointerId = e.pointerId;
      try { node.setPointerCapture(e.pointerId); } catch { /* unsupported browser */ }
      this._moveStick(state, e);
      e.preventDefault();
      e.stopPropagation();
    };
    const move = (e) => {
      if (e.pointerId !== state.pointerId) return;
      this._moveStick(state, e);
      e.preventDefault();
    };
    const up = (e) => {
      if (e.pointerId !== state.pointerId) return;
      this._releaseStick(state);
      e.preventDefault();
    };
    node.addEventListener('pointerdown', down);
    node.addEventListener('pointermove', move);
    node.addEventListener('pointerup', up);
    node.addEventListener('pointercancel', up);
    node.addEventListener('lostpointercapture', () => this._releaseStick(state));
    return state;
  }

  _moveStick(state, e) {
    const rect = state.node.getBoundingClientRect();
    const radius = Math.max(1, rect.width * 0.31);
    let x = e.clientX - (rect.left + rect.width * 0.5);
    let y = e.clientY - (rect.top + rect.height * 0.5);
    const len = Math.hypot(x, y);
    if (len > radius) {
      x = (x / len) * radius;
      y = (y / len) * radius;
    }
    state.thumb.style.transform = `translate(-50%, -50%) translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
    this.input.setTouchStick(state.kind, clamp(x / radius, -1, 1), clamp(y / radius, -1, 1));
  }

  _releaseStick(state) {
    if (state.pointerId === null) return;
    state.pointerId = null;
    state.thumb.style.transform = 'translate(-50%, -50%)';
    this.input.setTouchStick(state.kind, 0, 0);
  }

  _makeButton(name, label) {
    const node = el('button', `ow-touch-button ow-touch-button-${name}`, this.root, label);
    node.type = 'button';
    node.setAttribute('aria-label', `${label} button`);
    const state = { name, node, pointerId: null };
    const down = (e) => {
      if (!this.active || state.pointerId !== null) return;
      state.pointerId = e.pointerId;
      try { node.setPointerCapture(e.pointerId); } catch { /* unsupported browser */ }
      node.classList.add('ow-touch-held');
      this.input.setTouchButton(name, true);
      e.preventDefault();
      e.stopPropagation();
    };
    const up = (e) => {
      if (e.pointerId !== state.pointerId) return;
      this._releaseButton(state);
      e.preventDefault();
    };
    node.addEventListener('pointerdown', down);
    node.addEventListener('pointerup', up);
    node.addEventListener('pointercancel', up);
    node.addEventListener('lostpointercapture', () => this._releaseButton(state));
    return state;
  }

  _releaseButton(state) {
    if (state.pointerId === null) return;
    state.pointerId = null;
    state.node.classList.remove('ow-touch-held');
    this.input.setTouchButton(state.name, false);
  }

  _reset() {
    for (const stick of this.sticks ?? []) this._releaseStick(stick);
    for (const button of this.buttons ?? []) this._releaseButton(button);
    this.input.clearTouchControls();
  }

  dispose() {
    this._reset();
    this.media?.removeEventListener?.('change', this._onMediaChange);
    removeEventListener('blur', this._onBlur);
    this.root.remove();
  }
}
